'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { signIn, signOut, useSession } from 'next-auth/react';
import { POST_END_IMPERSONATION, POST_MASTER_OFFICE_ENTER } from '@/constants';
import { defaultLocale, supportedLocales } from '@/config/i18n/locales';
import { SKIP_KC_LOGOUT_COOKIE } from '@/lib/next-auth/constants';
import { runtimeConfig } from '@/lib';
import type { DecodedToken } from '@/types';
import { ImpersonatedBroker, MasterAdminContext } from './masterAdmin.context';
import { beginOfficeHandoff, endOfficeHandoff } from './officeHandoff';

const LS_KEY_BROKER = 'masterAdmin_impersonatedBroker';
/** sid of a master access token whose Keycloak session impersonation already ended. */
const LS_KEY_REVOKED_SID = 'masterAdmin_revokedSid';
const KEYCLOAK_REALM = 'myrealm';

let reloginInFlight = false;

function readRevokedSid(): string | null {
  try {
    return localStorage.getItem(LS_KEY_REVOKED_SID);
  } catch {
    return null;
  }
}

function rememberRevokedSid(sid: string) {
  try {
    localStorage.setItem(LS_KEY_REVOKED_SID, sid);
  } catch {
    // The next impersonation 401 still drops the app session.
  }
}

function forgetRevokedSid() {
  try {
    localStorage.removeItem(LS_KEY_REVOKED_SID);
  } catch {
    // The next successful login compares a new sid and removes it then.
  }
}

function tokenSid(accessToken: string): string | undefined {
  try {
    return jwtDecode<DecodedToken>(accessToken).sid;
  } catch {
    return undefined;
  }
}

function readBroker(): ImpersonatedBroker | null {
  try {
    const raw = localStorage.getItem(LS_KEY_BROKER);
    return raw ? (JSON.parse(raw) as ImpersonatedBroker) : null;
  } catch {
    return null;
  }
}

function armSkipKeycloakLogout() {
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${SKIP_KC_LOGOUT_COOKIE}=1; Path=/; Max-Age=60; SameSite=Lax${secure}`;
}

function localizedPath(path: string): string {
  const first = window.location.pathname.split('/').filter(Boolean)[0];
  if (first && first !== defaultLocale && (supportedLocales as readonly string[]).includes(first)) {
    return `/${first}${path}`;
  }
  return path;
}

/** Fresh Keycloak login. prompt=login must not reuse the office-admin SSO cookie. */
async function reloginMaster(): Promise<void> {
  if (reloginInFlight) return;
  reloginInFlight = true;
  try {
    await signIn(
      'keycloak',
      { callbackUrl: localizedPath('/profile/master-admin') },
      { prompt: 'login' },
    );
  } catch (error) {
    reloginInFlight = false;
    throw error;
  }
}

export const MasterAdminProvider = ({ children }: { children: React.ReactNode }) => {
  const { data: session, status } = useSession();
  const [impersonatedBroker, setImpersonatedBroker] = useState<ImpersonatedBroker | null>(null);
  const [resumeCheck, setResumeCheck] = useState(0);
  const handoff = useRef(false);

  const accessToken = session?.accessToken;

  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      reloginInFlight = false;
      handoff.current = false;
      setResumeCheck((n) => n + 1);
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  useEffect(() => {
    if (handoff.current || status === 'loading') return;
    if (status !== 'authenticated' || !accessToken) {
      // A leftover office-handoff flag must not start Keycloak on page load.
      endOfficeHandoff();
      localStorage.removeItem(LS_KEY_BROKER);
      setImpersonatedBroker(null);
      return;
    }
    endOfficeHandoff();
    let decoded: DecodedToken | undefined;
    try {
      decoded = jwtDecode<DecodedToken>(accessToken);
    } catch {
      decoded = undefined;
    }
    const revokedSid = readRevokedSid();
    if (decoded && revokedSid && decoded.sid === revokedSid && !decoded.impersonator) {
      localStorage.removeItem(LS_KEY_BROKER);
      setImpersonatedBroker(null);
      return;
    }
    if (revokedSid && decoded?.sid && decoded.sid !== revokedSid) {
      forgetRevokedSid();
    }
    if (!decoded?.impersonator) {
      localStorage.removeItem(LS_KEY_BROKER);
      setImpersonatedBroker(null);
      return;
    }
    setImpersonatedBroker(
      readBroker() ?? {
        id: decoded.sub ?? '',
        name: decoded.email || decoded.name,
        officeName: '',
      },
    );
  }, [accessToken, resumeCheck, status]);

  const loginAsBroker = useCallback(
    async (broker: ImpersonatedBroker) => {
      const masterAccessToken = session?.accessToken;
      if (!masterAccessToken) {
        console.error('[master-admin] session is missing the access token');
        return;
      }
      const authUrl = runtimeConfig.AUTH_URL;
      if (!authUrl) {
        console.error('[master-admin] AUTH_URL is not configured');
        return;
      }

      const sid = tokenSid(masterAccessToken);
      if (sid && sid === readRevokedSid()) {
        handoff.current = true;
        try {
          await reloginMaster();
        } catch (error) {
          handoff.current = false;
          console.error('[master-admin] re-login after revoked master session failed', error);
        }
        return;
      }

      beginOfficeHandoff();
      handoff.current = true;
      let appSessionDropped = false;
      try {
        // Drop the app session only. Keycloak logout here would kill the bearer
        // impersonation still needs. That call ends the master Keycloak session.
        armSkipKeycloakLogout();
        await signOut({ redirect: false });
        appSessionDropped = true;

        const bearer = { headers: { Authorization: `Bearer ${masterAccessToken}` } };
        await axios.post(
          `${POST_MASTER_OFFICE_ENTER}/${encodeURIComponent(broker.id)}/enter`,
          undefined,
          bearer,
        );
        // Keycloak sends no CORS headers on impersonation 401, so fetch rejects
        // and response.status is never visible. That rejection means the bearer is dead.
        let response: Response | null = null;
        try {
          response = await fetch(
            `${authUrl}/admin/realms/${KEYCLOAK_REALM}/users/${encodeURIComponent(broker.id)}/impersonation`,
            {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${masterAccessToken}`,
                Accept: 'application/json',
              },
              credentials: 'include',
              redirect: 'manual',
            },
          );
        } catch (error) {
          console.error('[master-admin] impersonation rejected the master session', error);
        }
        if (!response || response.status === 401 || response.status === 0) {
          if (sid) rememberRevokedSid(sid);
          await reloginMaster();
          return;
        }
        if (response.status >= 400) {
          throw new Error(`impersonation failed: ${response.status}`);
        }
        if (sid) rememberRevokedSid(sid);
        localStorage.setItem(LS_KEY_BROKER, JSON.stringify(broker));
        setImpersonatedBroker(broker);
        await signIn('keycloak', { callbackUrl: localizedPath('/profile/dashboard') });
      } catch (error) {
        localStorage.removeItem(LS_KEY_BROKER);
        setImpersonatedBroker(null);
        console.error('[master-admin] sign-in as office admin failed', error);
        if (!appSessionDropped) {
          handoff.current = false;
          endOfficeHandoff();
          return;
        }
        try {
          await reloginMaster();
        } catch (reloginError) {
          handoff.current = false;
          endOfficeHandoff();
          console.error(
            '[master-admin] re-login after revoked master session failed',
            reloginError,
          );
        }
      }
    },
    [session],
  );

  const returnToMasterAdmin = useCallback(async () => {
    handoff.current = true;
    try {
      await axios.post(POST_END_IMPERSONATION);
    } catch (error) {
      // The office session may still be alive. prompt=login below still forces the
      // Keycloak form, so a leftover SSO cookie cannot sign the office admin back in.
      console.error('[master-admin] end impersonation failed', error);
    }
    try {
      localStorage.removeItem(LS_KEY_BROKER);
      setImpersonatedBroker(null);
      await reloginMaster();
    } catch (error) {
      handoff.current = false;
      console.error('[master-admin] return to master panel failed', error);
    }
  }, []);

  const isImpersonating = impersonatedBroker !== null;

  return (
    <MasterAdminContext.Provider
      value={{
        isImpersonating,
        impersonatedBroker,
        loginAsBroker,
        returnToMasterAdmin,
      }}
    >
      {children}
    </MasterAdminContext.Provider>
  );
};
